import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { IndianRupee, Save, Truck } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["max_weight_kg", "Capacity (kg)"],
  ["base_price", "Base Price (₹)"],
  ["rate_per_km", "Rate / KM (₹)"],
  ["rate_per_kg", "Rate / KG (₹)"],
  ["min_order_value", "Min Order Value (₹)"],
];
const MULTS = [["standard", "Standard"], ["express", "Express"], ["economic", "Economic"]];

export function PDPricingSettings() {
  const [tiers, setTiers] = useState([]);
  const [mult, setMult] = useState({ standard: 1, express: 1.5, economic: 0.8 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/pd-pricing").then((r) => { setTiers(r.data.tiers); setMult(r.data.multipliers); }).catch((e) => toast.error(apiErr(e)));
  }, []);

  const setTier = (key, f, v) => setTiers((ts) => ts.map((t) => (t.key === key ? { ...t, [f]: v } : t)));

  const save = async () => {
    const body = {
      tiers: tiers.map((t) => ({ key: t.key, ...Object.fromEntries(FIELDS.map(([f]) => [f, Number(t[f])])) })),
      multipliers: Object.fromEntries(MULTS.map(([k]) => [k, Number(mult[k])])),
    };
    if (body.tiers.some((t) => FIELDS.some(([f]) => Number.isNaN(t[f]) || t[f] < 0))) return toast.error("Enter valid rates (0 or more)");
    if (Object.values(body.multipliers).some((v) => !v || v <= 0)) return toast.error("Multipliers must be greater than 0");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/pd-pricing", body);
      setTiers(data.tiers);
      setMult(data.multipliers);
      toast.success("Pricing saved — applies to all new quotes");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-border bg-card p-5 space-y-5" data-testid="pd-pricing-settings">
      <div>
        <div className="flex items-center gap-2"><IndianRupee className="h-4 w-4 text-primary" /><h2 className="font-heading font-bold uppercase tracking-tight">Pickup &amp; Drop Pricing</h2></div>
        <p className="text-xs text-muted-foreground mt-1">
          Total = (Distance × Rate/KM) + (Chargeable Weight × Rate/KG) + Base Price, then × Delivery Type multiplier. If below the tier's Minimum Order Value, the minimum is charged.
          The tier is picked automatically by chargeable weight (first tier whose capacity fits). Changes apply instantly to new quotes.
        </p>
      </div>

      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-muted text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-2">Truck Load Type</th>
              {FIELDS.map(([f, l]) => <th key={f} className="text-left p-2">{l}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tiers.map((t) => (
              <tr key={t.key} data-testid={`pd-tier-${t.key}`}>
                <td className="p-2 font-medium whitespace-nowrap"><Truck className="inline h-4 w-4 text-primary mr-1.5" />{t.label}</td>
                {FIELDS.map(([f]) => (
                  <td key={f} className="p-2">
                    <Input type="number" min="0" step="any" className="rounded-none font-mono-num h-9 w-28" value={t[f]}
                      onChange={(e) => setTier(t.key, f, e.target.value)} data-testid={`pd-tier-${t.key}-${f}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-widest mb-2">Delivery Type Multipliers</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {MULTS.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <label className="text-xs font-medium">{label} ×</label>
              <Input type="number" step="0.1" min="0.1" className="rounded-none font-mono-num" value={mult[k]} onChange={(e) => setMult((m) => ({ ...m, [k]: e.target.value }))} data-testid={`pd-mult-${k}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="button" disabled={saving} onClick={save} className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="pd-save-pricing"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Pricing"}</Button>
      </div>
    </section>
  );
}

export default PDPricingSettings;
